/**
 * ProposalDecisionBar - Faixa de fechamento (CTA final) da landing pública
 *
 * Reforça o valor total, a urgência da validade e concentra as ações de
 * aceite/recusa com destaque visual máximo.
 *
 * @version 1.0.0
 * RELEASE: 2026-08-11 v1.0.0 - Criação inicial (redesign premium da landing /p/:token)
 */

import React from 'react';
import { CheckCircle2, Clock, Lock, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { formatCurrency, validityLabel } from './proposalFormat';

interface ProposalDecisionBarProps {
  total: number;
  currency: string;
  remainingDays: number | null;
  onAccept: () => void;
  onReject: () => void;
}

export function ProposalDecisionBar({
  total,
  currency,
  remainingDays,
  onAccept,
  onReject,
}: ProposalDecisionBarProps) {
  const urgency = validityLabel(remainingDays);

  return (
    <section className="proposal-hero-surface proposal-grain proposal-reveal p-6 md:p-9">
      <div className="relative z-10 flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
        <div className="space-y-2">
          <h2 className="text-xl font-semibold md:text-2xl">Pronto para começar?</h2>
          <p className="max-w-md text-sm opacity-75">
            Ao aceitar, sua confirmação é registrada eletronicamente e o time comercial
            é notificado imediatamente para dar início ao atendimento.
          </p>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 pt-1 text-xs opacity-70">
            <span className="font-medium opacity-100">
              Total {formatCurrency(total, currency)}
            </span>
            {urgency && (
              <span className="inline-flex items-center gap-1.5">
                <Clock className="h-3.5 w-3.5" />
                {urgency}
              </span>
            )}
            <span className="inline-flex items-center gap-1.5">
              <Lock className="h-3.5 w-3.5" />
              Conexão segura
            </span>
          </div>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <Button
            variant="ghost"
            onClick={onReject}
            className="h-12 rounded-xl border border-white/15 px-5 text-white/75 hover:bg-white/10 hover:text-white"
          >
            <XCircle className="mr-2 h-4 w-4" />
            Recusar
          </Button>
          <Button
            onClick={onAccept}
            className="h-12 rounded-xl bg-white px-8 text-base font-semibold text-slate-900 shadow-lg transition-transform hover:scale-[1.02] hover:bg-white"
          >
            <CheckCircle2 className="mr-2 h-5 w-5" />
            Aceitar proposta
          </Button>
        </div>
      </div>
    </section>
  );
}
